import * as stylex from "@stylexjs/stylex";
import { CalendarPlus, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { copy } from "@/copy";
import "@/theme/tokens.stylex";
import { colors, motion, radii } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { Icon } from "@/ui/Icon";
import { IconButton } from "@/ui/IconButton";
import { PersonDot } from "@/ui/PersonDot";
import { press } from "@/ui/press";
import type { WeekDay } from "./WeekStrip";
type CalendarHeaderProps = {
  range: string;
  month: string;
  label: string;
  pickLabel: string;
  days: readonly WeekDay[];
  selected: string;
  today: string;
  onOpenPicker: () => void;
  onPrevious: () => void;
  onNext: () => void;
  onAddEvent: () => void;
  onSelect: (day: string) => void;
};
const BUTTON = 44;
const styles = {
  heading: {
    kzQI83: "x1iyjqo2",
    k7Eaqz: "xeuugli",
    kAzted: "xbktkl8",
    kB7OPa: "x9f619",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kjj79g: "xl56j7k",
    kGNEyG: "x1cy8zhl",
    kOIVth: "x1jnr06f",
    k8WAf4: "xt970qd",
    kZCmMZ: "x1g0dm76",
    kwRFfy: "xyri2b",
    kMzoRj: "xc342km",
    kWkggS: "xjbqb8w",
    kMwMTN: "x1heor9g",
    kVVagm: "xln7xf2",
    k9WMMc: "x1yc453h",
    kkrTdU: "x1ypdohk",
    k3nNDw: "x1nf803f",
    k1ekBW: "x11xpdln",
    kIyJzY: "x1bpocu2",
    kAMwcw: "x1em5f5i",
    $$css: true
  },
  button: {
    kzqmXN: "x187nhsf",
    kZKoxP: "xn3w4p2",
    $$css: true
  },
  day: {
    k7Eaqz: "xeuugli",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1jnr06f",
    kB7OPa: "x9f619",
    kLKAdn: "x1iorvi4",
    kGO01o: "x10b6aqq",
    kg3NbH: "xnjsko4",
    kMzoRj: "xc342km",
    kWkggS: "xjbqb8w",
    kMwMTN: "x1a11ngh",
    kVVagm: "xln7xf2",
    kkrTdU: "x1ypdohk",
    k1ekBW: "x11xpdln",
    kIyJzY: "x1bpocu2",
    kAMwcw: "x1em5f5i",
    $$css: true
  }
};
export function CalendarHeader({
  range,
  month,
  label,
  pickLabel,
  days,
  selected,
  today,
  onOpenPicker,
  onPrevious,
  onNext,
  onAddEvent,
  onSelect
}: CalendarHeaderProps) {
  return <div className="x2lah0s x78zum5 xdt5ytf x1jnr06f xeuugli x9f619 xz9dl7a xaope02 xwib8y2 xwe7p7i xh540kj x1a11ngh">
      <div className="x78zum5 x6s0dn4 x167g77z xeuugli">
        <button type="button" aria-haspopup="dialog" aria-label={pickLabel} onClick={onOpenPicker} {...stylex.props(styles.heading, press.scale)}>
          <span className="xr0iwhn xif65rj xbzybt3 x1u7k74 xtvhhri xuxw1ft">{range}</span>
          <span className="x78zum5 x6s0dn4 x17d4w8g xeuugli x193iq5w">
            <span className="x1pvqxga x1v94iar x1tup8yo x1159mfc xuxw1ft xb3r6kr xlyipyv">{month}</span>
            <Icon icon={ChevronDown} size={20} strokeWidth={2} />
          </span>
        </button>
        <IconButton icon={ChevronLeft} label={copy.calendar.previousWeek} onClick={onPrevious} xstyle={styles.button} />
        <IconButton icon={ChevronRight} label={copy.calendar.nextWeek} onClick={onNext} xstyle={styles.button} />
        <IconButton icon={CalendarPlus} label={copy.calendar.addEvent} onClick={onAddEvent} xstyle={styles.button} />
      </div>
      <fieldset aria-label={label} className="x1ghz6dp x1717udv xc342km xrvj5dj x1gj6vdr x195vfkc xeuugli">
        {days.map((day, index) => <MiniDay key={day.key} day={day} letter={copy.calendar.weekdayLetters[index] ?? ""} selected={day.key === selected} today={day.key === today} onClick={() => onSelect(day.key)} />)}
      </fieldset>
    </div>;
}
type MiniDayProps = {
  day: WeekDay;
  letter: string;
  selected: boolean;
  today: boolean;
  onClick: () => void;
};
function MiniDay({
  day,
  letter,
  selected,
  today,
  onClick
}: MiniDayProps) {
  return <button type="button" aria-label={day.name} aria-pressed={selected} aria-current={today ? "date" : undefined} onClick={onClick} {...stylex.props(styles.day, press.scale)}>
      <span className="xr0iwhn x4z9k3i xbzybt3 x1d3mw78 xtvhhri">{letter}</span>
      <span {...{
      0: {
        className: "x2lah0s xh8yej3 x1k2d6hx x1plog1 xrvj5dj x1ku5rj1 xqhrb7l xwsyq91 x1cf7cre x1roq8pg xo5v014 xss6m8b xs2xxs2 x16c7our xwji4o3"
      },
      2: {
        className: "x2lah0s xh8yej3 x1k2d6hx x1plog1 xrvj5dj x1ku5rj1 xqhrb7l xwsyq91 x1cf7cre x1roq8pg xo5v014 xss6m8b xs2xxs2 x16c7our xwji4o3 x5r8ks9"
      },
      1: {
        className: "x2lah0s xh8yej3 x1k2d6hx x1plog1 xrvj5dj x1ku5rj1 xqhrb7l xwsyq91 x1cf7cre x1roq8pg xo5v014 xss6m8b xs2xxs2 x16c7our xwji4o3 xfenp3h xnnvkky"
      },
      3: {
        className: "x2lah0s xh8yej3 x1k2d6hx x1plog1 xrvj5dj x1ku5rj1 xqhrb7l xwsyq91 x1cf7cre x1roq8pg xo5v014 xss6m8b xs2xxs2 x16c7our xwji4o3 xfenp3h xnnvkky"
      }
    }[!!(today && !selected) << 1 | !!selected << 0]}>
        {day.date}
      </span>
      <span className="x78zum5 x6s0dn4 x1ed6fcf xdk7pt">
        {day.dots.map(dot => <span key={dot.id} data-testid="week-dot" className="x78zum5">
            <PersonDot color={dot.color} size="xs" />
          </span>)}
      </span>
    </button>;
}